import type { TableHeader } from '@/types';

export const MARKETSPARK = 'MarketSpark';

export const MARKETSPARK_EMAIL = '@marketspark.com';
export const MARKETSPARK_SUPPORT_NUMBER = '18443355153';

export const APP_NAME = import.meta.env.VITE_EIKON_APP_NAME || 'Command Center';
export const TEAM_PORTAL_URL =
  import.meta.env.VITE_TEAM_PORTAL_APP_URL || 'https://tools.marketspark.com';
export const API_URL = import.meta.env.VITE_CMDC_API_URL;

export const DELETE_TEXT = 'delete';
export const CONFIRM_TEXT = 'Confirm';

export const AuthErrors = {
  NO_API: 'Failed to reach the API',
  TOKEN_ERROR: 'Failed to fetch user token',
  NO_USER_DT: 'Failed to find user details',
} as const;

export type AuthErrorKey = keyof typeof AuthErrors;

export enum FormTexts {
  Delete = 'Delete',
  Confirm = 'Confirm',
  Cancel = 'Cancel',
  Close = 'Close',
  Reset = 'Reset',
  Back = 'Back',
  Next = 'Next',
  Edit = 'Edit',
  Post = 'Post',
  Download = 'Download',
}

export enum API_URLS {
  apiCredentials = '/users/me/api-credentials',
  apn = '/apn',
  accounts = '/accounts',
  auditLogs = '/audit-logs',
  authroles = '/auth-roles',
  bundleCatalog = '/bundle-catalog',
  carriers = '/carriers',
  catalogRestrictions = '/brand-mac-prefixes',
  cdrs = '/cdrs',
  contacts = '/contacts',
  dataExport = '/data-export',
  divisions = '/divisions',
  docs = '/docs',
  equipment = '/equipment',
  gateway = '/gateways',
  grandstream = '/grandstream',
  hardwareCatalog = '/hardware-catalogs',
  installations = '/installations',
  lines = '/dids',
  lineTypes = '/line-types',
  locations = '/locations',
  location = '/location',
  metrics = '/metrics',
  notificationSubscriptions = '/notification-subscriptions',
  organizations = '/organizations',
  phoneNumbers = 'phone-numbers',
  powersource = '/powersources',
  router = '/routers',
  salesOrderMetadata = '/sales-order-metadata',
  salesOrderCatalog = '/sales-order-catalog',
  support = '/support',
  sims = '/sims',
  users = '/users',
}

export enum APP_URLS {
  activityLogs = 'activity',
  auth = 'auth',
  bundles = 'hardware/bundles',
  cdr = 'cdr',
  contacts = 'contacts',
  dashboard = 'dashboard',
  decommission = 'decommission',
  divisions = 'divisions',
  docs = 'docs',
  gateways = 'hardware/gateways',
  lines = 'lines',
  locations = 'locations',
  notifications = 'notifications',
  organizations = 'organizations',
  powersources = 'hardware/powersources',
  profile = 'profile',
  routers = 'hardware/routers',
  users = 'users',
  hardware = 'hardware',
}

export enum CRUD {
  create = 'create',
  update = 'update',
  edit = 'edit',
  delete = 'delete',
}

export enum ROLE_NAMES {
  superAdmin = 'Super Admin',
  orgAdmin = 'Org Admin',
  locationAdmin = 'Location Admin',
  user = 'User',
}

export enum ORGANIZATION_TABS {
  general = 'general',
  data = 'data',
  notifications = 'notifications',
}

// TODO: Api Credentials URL
export enum HELP_URLS {
  GettingStarted = 'https://marketspark.atlassian.net/wiki/spaces/DOCS/pages/2540863494/Getting+Started',
  Installation = 'https://marketspark.atlassian.net/wiki/spaces/DOCS/pages/2540109833/Installation+Support',
  HowTo = 'https://marketspark.atlassian.net/wiki/spaces/DOCS/pages/edit-v2/2540371972',
  Divisions = 'https://marketspark.atlassian.net/wiki/spaces/DOCS/pages/2486763559/Divisions+Feature',
}
export const BASIFY = (url: string) => {
  return `/${url}`;
};

export const CDR_DAYS = 30;

export enum DidType {
  PERM = 'Perm DID',
  TEMP = 'Temp DID',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tableStatusColumn = (column: string, sortable = true): TableHeader<any> => {
  return {
    title: 'Status',
    key: column,
    width: '62px',
    sortable: sortable,
  };
};

export enum CallDirection {
  All = 'all',
  Inbound = 'inbound',
  Outbound = 'outbound',
}

export enum CallDirectionUI {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
}

export const MIX_PORTAL_URL = 'https://my.mixnetworks.com/portal/domains/manage/';

export const N2P_PORTAL_URL = 'https://ncthv.net2phone.com/portal/domains/manage/';

export const MIX_DOMAIN_REGEX = new RegExp(/^(?:[a-zA-Z0-9]+\.){2}pbx\.supersip\.net$/);

export const GATEWAY_METRIC_BRANDS = {
  GRANDSTREAM: 'grandstream',
};

export const ROUTER_METRIC_BRANDS = {
  CRADLEPOINT: 'cradlepoint',
  INHAND: 'inhand',
  ERICSSON: 'ericsson',
};

export const ERICSSON_BRANDS = [ROUTER_METRIC_BRANDS.CRADLEPOINT, ROUTER_METRIC_BRANDS.ERICSSON];

export const SUPPORTED_COUNTRY_CODES_MAP: { [key: string]: string } = {
  ['USA']: 'US',
  ['Canada']: 'CA',
  ['Puerto Rico']: 'PR',
};

export enum USER_ROLE {
  SUPER_ADMIN = 'Super Admin',
  ORG_ADMIN = 'Org Admin',
  LOCATION_ADMIN = 'Location Admin',
  INSTALL_PARTNER = 'Install Partner',
  SALES_PARTNER = 'Sales Partner',
}

export enum CONTACT_TYPE {
  PRIMARY = 'Primary Contact',
  SECONDARY = 'Secondary Contact',
  INSTALL = 'Install Manager',
  SERVICE = 'Service Owner',
  SALES = 'Sales Owner',
}

export enum ORG_STATUS {
  DECOMMISIONED = 'Decommissioned',
  IN_CONTRACT = 'In Contract',
  ON_HOLD = 'On Hold',
  PILOT = 'Pilot',
  COMMERCIAL = 'Commercializing',
  SUSPENDED = 'suspended',
}

export enum LOC_STATUS {
  Provisioning = 'Provisioning',
  Shipping = 'Shipping',
  Operational = 'Operational',
  NeedsService = 'Needs Service',
  NotOperational = 'Not Operational',
  Suspended = 'Suspended',
  Decom = 'Decommissioned',
}

export const MS_OR_CUSTOMER_OPTIONS = [
  { title: MARKETSPARK, value: MARKETSPARK },
  { title: 'Customer', value: 'Customer' },
];

export const selectAllValue = '__ALL__';
export const selectAllOption = {
  title: 'Select All',
  value: selectAllValue,
  disabled: false,
};

export enum SipProviders {
  n2p = 'net2phone',
  mix = 'mixnetworks',
}

export const SipProviderOptions = [
  { title: 'net2phone', value: SipProviders.n2p },
  { title: 'MIX Networks', value: SipProviders.mix },
];

export const ServiceStatuses = ['total', 'online', 'offline', 'trouble', 'none'];

export enum ServiceStatusEnum {
  TOTAL = 'total',
  ONLINE = 'online',
  OFFLINE = 'offline',
  TROUBLE = 'trouble',
  NONE = 'none',
}

// TODO: Refactor mac-brand-prefixes to be more abstract so we don't need to do the below
export const InHandModels = ['IR302', 'IR305S', 'IR615S', 'IR315'];

export const InhandModelsWithoutRssi = ['IR315'];

export const AntennaPattern = /^Antenna w\/\d+' cable \((indoor|outdoor)\)$/i;

export const HousingPattern = /^\d+MM Enclosure$/i;

export const ExtensionPattern = /\b(extension cable|cable extension)\b/i;

export enum AcessoriesLabelEnum {
  HOUSING = 'Housing',
  ANTENNA = 'Antenna',
  EXTENSION = 'Extension',
}

export enum SalesOrderCatalogGroupEnum {
  COMPONENT = 'Component',
  ANTENNA = 'Antenna',
}

export const contactUsTopicItems = [
  'Account',
  'Command Center',
  'Service Issue',
  'Records Request',
  'Other',
];

export enum SubscriptionTypeEnum {
  STATUS = 'status',
  CDR = 'cdr',
}

export const SUPPORT_PHONE_NUMBER = '18443355153';

export const MAX_DID_DESCRIPTION_LENGTH = 32;
